import { useUser } from '@clerk/nextjs';
import { useEffect } from 'react';
import { toast } from 'react-toastify';
import Intercom from '@intercom/messenger-js-sdk';
import { useUserContext } from '~/react_context/UserContext';

/**
 * Single source of truth for whether the Intercom messenger should be active. Used both here (to decide
 * whether to boot) and by the custom launcher button (to decide whether to render), so the two never drift.
 */
export function shouldUseIntercom({ impersonatingUser, isTestUser }: { impersonatingUser: string | null; isTestUser: boolean }) {
	if (process.env.VERCEL_ENV !== 'production' && isTestUser) return false;
	if (impersonatingUser !== null) return false;
	return true;
}

export default function useIntercomBoot({ intercomAppId }: { intercomAppId: string }) {
	const { user } = useUser();
	const { businessName, businessId, impersonatingUser } = useUserContext();
	const isTestUser = Boolean(user?.publicMetadata.isTestUser);

	useEffect(() => {
		if (process.env.VERCEL_ENV !== 'production' && isTestUser) {
			// publicMetadata.isTestUser can be set in Clerk profile
			console.log('Logged in as Cypress test user. No need for intercom.');
		} else if (impersonatingUser !== null) {
			toast.success(`Impersonating ${impersonatingUser} - Intercom disabled`, {
				position: 'bottom-right',
				autoClose: 3000,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: false,
				draggable: true,
				progress: undefined,
				theme: 'light',
			});
		} else {
			Intercom({
				app_id: intercomAppId,
				company: {
					company_id: String(businessId) ?? undefined,
					name: businessName ?? undefined,
				},
				email: user?.primaryEmailAddress?.emailAddress,
				// Hide Intercom's native launcher; <DraggableIntercomLauncher /> renders our own draggable button
				// and keeps the panel anchored via Intercom('update', { alignment, *_padding }).
				hide_default_launcher: true,
				name: user?.fullName ?? undefined,
				phone: user?.primaryPhoneNumber?.phoneNumber,
				user_id: user?.id,
			});
		}
	}, [user?.id, impersonatingUser, isTestUser]);
}
